import { ITableColumn } from "components/interfaces";
import { computed, makeObservable } from "mobx";
import { AdvanceStore, MeasurementStore, MessageStore } from ".";
import AdvanceController from "../api/controllers/AdvanceController";
import { RequiredParamException } from "../api/exceptions/InvalidParamException";
import { GreaterThanOrEqualException } from "../api/exceptions/InvalidValueException";
import AdvanceModel from "../api/models/AdvanceModel";
import InstallmentsLinkModel from "../api/models/InstallmentsLinkModel";
import { lang } from "../i18nLang";
import { formatCode } from "../utils";

class Store {
  public readonly columns: ITableColumn[] = [
    {
      dataKey: "input",
      label: "",
      align: "center",
      width: 50,
    },
    {
      dataKey: "title",
      label: lang("fields.title"),
      width: 150,
    },
    /* Os valores fixos sao necessarios para manter a tabela alinhada */
    {
      dataKey: "total",
      label: lang("fields.value"),
      width: 150,
      monetary: true,
      align: "right",
    },
    {
      dataKey: "balance",
      label: lang("fields.balance"),
      width: 150,
      monetary: true,
      align: "right",
    },
    {
      dataKey: "maturityDate",
      label: lang("fields.maturity"),
      width: 96,
      align: "center",
    },
  ];

  public readonly subColumns: ITableColumn[] = [
    {
      dataKey: "input",
      label: "",
      width: 50,
      align: "right",
    },
    {
      dataKey: "title",
      label: "",
      width: 150,
    },
    {
      dataKey: "value",
      label: "",
      width: 150,
      monetary: true,
      align: "right",
    },
    {
      dataKey: "balance",
      label: "",
      width: 150,
      align: "center",
    },
    {
      dataKey: "maturityDate",
      label: "",
      width: 96,
      align: "center",
    },
  ];

  private _advancesLinks: InstallmentsLinkModel<AdvanceModel>;

  constructor() {
    this._advancesLinks = new InstallmentsLinkModel<AdvanceModel>();

    makeObservable(this, {
      rows: computed,
      advancesLinks: computed,
    });
  }

  get advancesLinks() {
    return this._advancesLinks.links;
  }

  get rows() {
    const measurementId = MeasurementStore.currentMeasurementUid;

    const formatInstallments = (installments) =>
      installments.map((installment) => {
        const title = lang(
          "fields.installmentNumber",
          `${installment.id}/${installments.length}`
        );
        const maturityDate =
          installment.maturityDate.toLocaleDateString("pt-br");

        const initialCheck = {
          checked: Boolean(installment.measurementId),
          disabled:
            installment.measurementId &&
            installment.measurementId !== measurementId,
        };

        return {
          ...installment,
          title,
          initialCheck,
          balance: "-",
          maturityDate,
        };
      });

    return AdvanceStore.advances.map((advance) => ({
      id: advance.uid,
      code: formatCode(advance.code),
      title: advance.title,
      date: advance.getDate()?.toLocaleDateString("pt-br"),
      balance: advance.balanceWithoutMeasurement,
      total: advance.total,
      maturityDate: "-",
      installments: formatInstallments(advance.installments),
    }));
  }

  reset() {
    this._advancesLinks.clear();
  }

  getCurrentLinks() {
    const measurement = MeasurementStore.currentMeasurement;
    if (!measurement) return;

    measurement.advances.forEach((advance) => {
      this.addItemToAdvanceLink(advance.advanceId, advance.installment);
    });
  }

  addItemToAdvanceLink(advanceId, installmentId) {
    const advanceFound = this._advancesLinks.findLink(advanceId);

    if (advanceFound) {
      this._advancesLinks.addInstallmentId(advanceFound, installmentId);
      return;
    }

    const advance = AdvanceStore.findAdvance(advanceId);
    if (advance) {
      this._advancesLinks.addLink(advance, installmentId);
    }
  }

  removeItemFromAdvanceLink(advanceId, installmentId) {
    this._advancesLinks.removeItem(advanceId, installmentId);
  }

  updateAdvancesLinkWithMeasurement() {
    const measurement = MeasurementStore.currentMeasurement;
    if (!measurement) return;

    return AdvanceController.linkAdvancesToMeasurement(
      measurement,
      this._advancesLinks.links
    )
      .then(() => {
        MessageStore.success(lang("advances.success.linked"));
        return;
      })
      .catch((error) => {
        console.error(error);

        if (error instanceof RequiredParamException) {
          MessageStore.info(lang("advances.error.noLink"));
        } else if (
          error instanceof GreaterThanOrEqualException &&
          error.field === "netTotal"
        ) {
          MessageStore.error(lang("measurements.error.invalidTotal.advances"));
        } else {
          MessageStore.error(lang("advances.error.onLinkMeasurement"));
        }
        return true;
      });
  }
}

const AdvanceLinkStore = new Store();
export default AdvanceLinkStore;
